/**
 * "Import messages" dialog of the folder menu: .eml files are uploaded one request each (at most two at a time) and
 * scanned by the server before they are stored (fail closed). The list shows progress and, per file, the stored result
 * or the reason it was refused. The limits are checked here first to explain them before uploading; the server checks again.
 */
import { useI18n } from '@platform/i18n';
import { Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, errorCode, formatBytes, useErrorText } from '@platform/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Folder } from './api';

/** Keep in step with services/platform-api/src/modules/mailbox/import.ts (the server enforces them too). */
const IMPORT_MAX_BYTES = 25 * 1024 * 1024;
const IMPORT_MAX_FILES = 20;
const CONCURRENCY = 2;

type Status = 'queued' | 'uploading' | 'done' | 'failed';

interface Entry {
  key: number;
  file: File;
  status: Status;
  progress: number;
  error?: string | undefined;
}

const REFUSALS = {
  import_too_large: 'webmail.import.tooLarge',
  import_msg_invalid: 'webmail.import.msgInvalid',
  import_unsupported_type: 'webmail.import.unsupportedType',
  import_not_a_message: 'webmail.import.notAMessage',
  import_empty: 'webmail.import.notAMessage',
  import_held: 'webmail.import.held',
  attachment_infected: 'webmail.import.infected',
  attachment_blocked: 'webmail.import.blocked',
  attachment_scan_unavailable: 'webmail.import.scanUnavailable',
} as const;

export interface ImportMessagesDialogProps {
  folder: Folder;
  onClose: () => void;
  /** Called once after files were stored, so the lists can reload. */
  onImported: () => void;
}

export function ImportMessagesDialog({ folder, onClose, onImported }: ImportMessagesDialogProps) {
  const { t, formatNumber } = useI18n();
  const errorText = useErrorText();
  const input = useRef<HTMLInputElement>(null);
  const [entries, setEntries] = useState<readonly Entry[]>([]);
  const [notice, setNotice] = useState<string>();
  const nextKey = useRef(0);
  const running = useRef(new Set<number>());
  const stored = useRef(0);
  const patch = useCallback((key: number, change: Partial<Entry>) => setEntries((list) => list.map((e) => (e.key === key ? { ...e, ...change } : e))), []);

  const refusal = useCallback(
    (name: string, error: unknown): string => {
      const code = errorCode(error);
      const key = code === undefined ? undefined : REFUSALS[code as keyof typeof REFUSALS];
      return key ? t(key, { name, max: formatBytes(IMPORT_MAX_BYTES, formatNumber) }) : `${t('webmail.import.failed', { name })} ${errorText.describe(error)}`;
    },
    [t, formatNumber, errorText],
  );

  // Starts queued files while fewer than CONCURRENCY run.
  useEffect(() => {
    for (const entry of entries) {
      if (entry.status !== 'queued' || running.current.size >= CONCURRENCY || running.current.has(entry.key)) continue;
      running.current.add(entry.key);
      patch(entry.key, { status: 'uploading' });
      void (async () => {
        try {
          await api.importMessage?.(folder.id, entry.file, { onProgress: (fraction) => patch(entry.key, { progress: fraction }) });
          stored.current += 1;
          patch(entry.key, { status: 'done', progress: 1 });
        } catch (error) {
          patch(entry.key, { status: 'failed', error: refusal(entry.file.name, error) });
        } finally {
          running.current.delete(entry.key);
          setEntries((list) => [...list]); // wakes the scheduler for the next queued file
        }
      })();
    }
  }, [entries, folder.id, patch, refusal]);

  const close = () => {
    if (stored.current > 0) onImported();
    onClose();
  };

  const add = (files: File[]) => {
    setNotice(undefined);
    const room = IMPORT_MAX_FILES - entries.length;
    if (files.length > room) setNotice(t('webmail.import.tooManyFiles', { max: IMPORT_MAX_FILES }));
    const added: Entry[] = files.slice(0, Math.max(room, 0)).map((file) => {
      const lower = file.name.toLowerCase();
      const error = file.size > IMPORT_MAX_BYTES
        ? t('webmail.import.tooLarge', { name: file.name, max: formatBytes(IMPORT_MAX_BYTES, formatNumber) })
        : lower.endsWith('.eml') || lower.endsWith('.msg')
          ? undefined
          : t('webmail.import.unsupportedType', { name: file.name });
      nextKey.current += 1;
      return { key: nextKey.current, file, status: error ? 'failed' : 'queued', progress: 0, error } satisfies Entry;
    });
    setEntries((list) => [...list, ...added]);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('webmail.import.title', { name: folder.name })}</DialogTitle>
          <DialogDescription>{t('webmail.import.description', { max: formatBytes(IMPORT_MAX_BYTES, formatNumber), count: IMPORT_MAX_FILES })}</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          <input
            ref={input}
            type="file"
            multiple
            accept=".eml,.msg,message/rfc822"
            className="hidden"
            aria-label={t('webmail.import.chooseFiles')}
            data-testid="import-input"
            onChange={(event) => {
              add([...(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />
          <div>
            <Button type="button" variant="outline" onClick={() => input.current?.click()} disabled={entries.length >= IMPORT_MAX_FILES}>
              {t('webmail.import.chooseFiles')}
            </Button>
          </div>
          {notice && <p role="alert" className="text-sm text-destructive">{notice}</p>}
          {entries.length > 0 && (
            <ul className="flex flex-col gap-2" aria-label={t('webmail.import.listLabel')}>
              {entries.map((entry) => (
                <li key={entry.key} className="flex flex-col gap-1 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{entry.file.name}</span>
                    <span className="shrink-0 text-muted-foreground">{t(`webmail.import.status.${entry.status}`)}</span>
                  </div>
                  {entry.status === 'uploading' && (
                    <progress className="h-1 w-full" value={entry.progress} max={1} aria-label={t('webmail.import.progress', { name: entry.file.name })} />
                  )}
                  {entry.error && <p role="alert" className="text-destructive">{entry.error}</p>}
                </li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={close}>{t('webmail.import.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
